import { createNanoEvents, type Emitter, type EventsMap } from 'nanoevents'
import type { BroadcastChannel } from 'broadcast-channel'
import { logger as defaultLogger } from 'v2/utils/logger'

// Snippet taken from https://github.com/ai/nanoevents?tab=readme-ov-file#typescript
export class EventBus<Events extends EventsMap = EventsMap> {
  emitter: Emitter

  logger: typeof defaultLogger

  channel?: BroadcastChannel

  constructor(logger: typeof defaultLogger = defaultLogger, channel?: BroadcastChannel) {
    this.emitter = createNanoEvents<Events>()
    this.logger = logger
    this.logger.debug('EventBus initialized')
    this.channel = channel
    if (this.channel) {
      this.channel.onmessage = (message) => {
        this.logger.log('EventBus: broadcast message', message)
        this.emitter.emit(message.event, ...message.args)
      }
    }
  }

  on<E extends keyof Events>(event: E & (string | number), callback: Events[E]) {
    return this.emitter.on(event, callback)
  }

  // until the condition is met, the callback will be called
  until<E extends keyof Events>(
    event: E & (string | number),
    condition: (...args: Parameters<Events[E]>) => boolean,
    callback: Events[E],
  ): () => void {
    const unbind = this.emitter.on(event, (...args: Parameters<Events[E]>) => {
      if (condition(...args)) {
        unbind()
        callback(...args)
      }
    })
    return unbind
  }

  // unbinds the callback after the timeout
  within<E extends keyof Events>(
    event: E & (string | number),
    callback: Events[E],
    timeout: number,
  ) {
    const unbind = this.once(event, ((...args: Parameters<Events[E]>) => {
      clearTimeout(timeoutId)
      callback(...args)
    }) as Events[E])

    const timeoutId = setTimeout(() => {
      unbind()
      this.logger.debug(`Event ${String(event)} timed out after ${timeout}ms`)
    }, timeout)

    return () => {
      clearTimeout(timeoutId)
      unbind()
    }
  }

  emit<E extends keyof Events>(event: E & (string | number), ...args: Parameters<Events[E]>) {
    this.logger.log(`Event: ${event} with args: `, args)
    return this.emitter.emit(event, ...args)
  }

  // emits an event after a timeout
  emitAfter<E extends keyof Events>(
    event: E & (string | number),
    timeout: number,
    ...args: Parameters<Events[E]>
  ) {
    const timeoutId = setTimeout(() => {
      this.emitter.emit(event, ...args)
    }, timeout)
    return () => clearTimeout(timeoutId)
  }

  // broadcasts to all tabs except current tab
  broadcast<E extends keyof Events>(event: E & (string | number), ...args: Parameters<Events[E]>) {
    this.logger.log(`EventBus: broadcast - event: ${event} with args: `, args)
    return this.channel?.postMessage({ event, args })
  }

  // broadcasts to all tabs including self
  // because broadcast api does not publish to current tab
  broadcastToAll<E extends keyof Events>(
    event: E & (string | number),
    ...args: Parameters<Events[E]>
  ) {
    this.logger.log(`EventBus: broadcastSelf - event: ${event} with args: `, args)
    this.broadcast(event, ...args)
    return this.emitter.emit(event, ...args)
  }

  once<E extends keyof Events>(event: E & (string | number), callback: Events[E]) {
    const unbind = this.emitter.on(event, (...args) => {
      unbind()
      callback(...args)
    })
    return unbind
  }
}
